import { FunctionComponent, memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigContext, UserContext } from '../../../../context';
import {
  DeeplinkType,
  Select,
  SelectItem,
  useIsNewslettersAllowed,
  useIsPersonalizationEnabled,
} from '../../../../shared';

interface DeeplinkTargetSelectProps {
  value: DeeplinkType | null;
  onChange: (value: DeeplinkType | null) => void;
}

export const DeeplinkTargetSelect: FunctionComponent<DeeplinkTargetSelectProps> = memo(
  ({ value, onChange }) => {
    const { t } = useTranslation();

    const { leaderBoardBadge } = useContext(UserContext).userProfile;

    const isEventsEnabled = useContext(ConfigContext).config.elements.events?.enabled;

    const isPersonalizationEnabled = useIsPersonalizationEnabled();

    const isNewslettersAllowed = useIsNewslettersAllowed();

    const items = useMemo(
      (): SelectItem<DeeplinkType | null>[] => [
        { id: null, title: t('createNotificationModal.deeplink-target-CUSTOM') },
        ...[
          { target: DeeplinkType.BOOKMARKS, isAvailable: true },
          { target: DeeplinkType.EVENTS, isAvailable: Boolean(isEventsEnabled) },
          { target: DeeplinkType.MY_TOPICS, isAvailable: isPersonalizationEnabled },
          { target: DeeplinkType.PUSH_NOTIFICATIONS, isAvailable: true },
          { target: DeeplinkType.NEWSLETTERS, isAvailable: isNewslettersAllowed },
          { target: DeeplinkType.USER_PROFILE, isAvailable: true },
          { target: DeeplinkType.LEADERBOARD, isAvailable: Boolean(leaderBoardBadge) },
        ]
          .filter(({ isAvailable }) => isAvailable)
          .map(({ target }) => ({
            id: target,
            title: t(`createNotificationModal.deeplink-target-${target}`),
          })),
      ],
      [isEventsEnabled, isNewslettersAllowed, isPersonalizationEnabled, leaderBoardBadge, t]
    );

    return (
      <Select
        label={t('createNotificationModal.deeplink-target')}
        selectedItemId={value}
        items={items}
        onChange={(id) => onChange(id ?? null)}
        staticListPosition
      />
    );
  }
);
